import React, { Fragment, useEffect, useContext, useState, useRef } from "react";
import cn from "classnames";
import { EmitAnalyticsEvent } from "module/analytics/application/EmitAnalyticsEvent";
import { formatNumber } from "utils/formators";
import { useParams, useHistory } from "react-router-dom";
import { Tabs } from "shared/models";
import { EDDM_SIGNUP, CAMPAIGN_SUBTYPES_ENUM } from "shared/constants";
import { useTheme, useMediaQuery } from "@material-ui/core";
import { ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { DialogModalContext } from "components/dialog-modal";
import { If, IfElse } from "components/logic";
import { getTimeRange } from "components/date-picker/utils";
import { TickCircle } from "components/icons/components/TickCircle";
import CONFIG from "config/config";
import { CAMPAIGN_INTERNAL_STATUSES, CAMPAIGN_CHANNELS_MIN_RECIPIENTS } from "pages/constants";
import { useGetFlyerInfo } from "pages/flyer/flyerTypePage/useGetFlyerInfo";
import { FlyerStatus } from "pages/flyer/flyerTypePage/useGetDataForPage";
import { useEDDMCostsCalculation } from "Hooks/useEDDMCostsCalculation";
import CopilotLauncher from "copilot/CopilotLauncher";
import { ThemeToggle } from "campaign-builder/shell/ThemeToggle";
import { UserMenu } from "campaign-builder/channels/directmail/adm/UserMenu";
import { FlyerTypeModal } from "./components/EDDMSummary/FlyerTypeModal";
import { useStore } from "../../store";
import { EDDMSummary } from "./components/EDDMSummary";
import { StoreType } from "../../store/types";
import { authorization } from "../../module/auth";
import { CurrentStepIcon } from "../../static/media/current-step";
import { VerificationAlert } from "./components/VerificationAlert";
import { HeaderProps } from "./headerTypes";
import { MinimumResidencesModal } from "../../components/modals/MinimumResidencesModal";
import { EddmStepBillingGate } from "../../campaign-builder/checkout/billing-profile/EddmStepBillingGate";

// Step-nav "future/not-reached" marker — mirrors the redesigned StepHeader's
// FutureCircle so the EDDM topbar reads identically (hollow slate ring).
const FutureCircle = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10.5" stroke="#cbd5e1" strokeWidth="1.5" className="dark:stroke-slate-600" />
  </svg>
);

// Completed-step marker — same TickCircle + dark treatment the StepHeader uses.
const CompletedStepIcon = () => (
  <TickCircle fill="#2E3860" size={22} className="dark:[&_path]:fill-slate-300 dark:[&_circle]:fill-slate-900" />
);

export const EDDMHeader: React.FC<HeaderProps> = ({
  tabs = [],
  activeTabIndex,
  hideBackButton,
  headerActions,
  isUploadPage = false,
  isSubmitted,
  isNextActive,
  hasError = false,
  isDM = false,
  setShouldHideStatistics,
}) => {
  const runDialogModal = useContext(DialogModalContext);
  const posthog = usePostHog();
  const params = useParams();
  const history = useHistory();
  const { pageState, isQrCodeExist, isLoading, flyerInfo } = useGetFlyerInfo();
  const [isInfoDropdownOpen, setIsInfoDropdownOpen] = useState<boolean>(false);
  const [flyerSelectModalOpen, setFlyerSelectModalOpen] = useState<boolean>(false);
  const { recalculateEDDMCampaignCost } = useEDDMCostsCalculation();
  const [isOpenWarningModal, setIsOpenWarningModal] = useState<boolean>(false);
  const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);
  const prevModalOpenRef = useRef(isOpenWarningModal);

  const handleGoToTab = (href: string) => {
    const link = href.replace(":clientId", params.clientId).replace(":campaignId", params.campaignId).replace("?", "");
    history.push(link);
  };

  const currentTab = tabs.find((tab: Tabs) => tab.index === activeTabIndex);
  // Identity is keyed off the stable `step` field (not the display title) so the
  // topbar labels can be renamed (Targeting/Design/Tracking) without breaking
  // the verification alert or the segments cost dropdown.
  const isSubmitPage = currentTab?.step === "submit";
  const isSegmentsPage = currentTab?.step === "segments";

  const {
    user: { id: userId, clientId, firstName, lastName, email, accessDenied = true, phone, role, isVerified },
    client,
    campaign,
    costsCalculationData: { detailedCost, flyersAmount },
    country,
    errors: { notExistDesignFile },
    distributionLocations,
  }: StoreType = useStore();

  const { name: clientName } = client;

  const isEDDMsignup = client?.createdBy === EDDM_SIGNUP;

  const { id: campaignId, internalStatus, flyerType } = campaign;

  const { subtotal } = detailedCost || {};

  const postHogHeaderInfo = {
    user_id: userId,
    campaign_id: campaignId,
    company_id: clientId,
    cb_step: tabs.find((tab: Tabs) => tab.index === activeTabIndex)?.step,
  };

  const handleBackClick = () => {
    posthog.capture("Clicked on CB back button", postHogHeaderInfo);
    headerActions?.BACK?.action();
  };

  useEffect(() => {
    if (posthog && userId) {
      posthog.identify(userId, {
        role,
        client_id: clientId,
        user_id: userId,
        firstname: firstName,
        lastname: lastName,
        country: country?.name,
        email,
        phone,
      });
    }
  }, [userId, role, clientId, firstName, lastName, country, email, phone, country]);

  useEffect(() => {
    recalculateEDDMCampaignCost();
  }, [flyerType]);

  const completedStep = tabs.find((item: any) => item.step === campaign.lastActiveStep);

  // `lastActiveStep` is a breadcrumb the wizard writes as a person walks it, and
  // it is the wrong thing to gate navigation on by itself for two reasons.
  //
  // It can hold a value that is not one of these tabs at all — a campaign built
  // through the API sits at "channel", so the lookup above returns undefined and
  // EVERY forward tab goes dead, even on a campaign that is complete and
  // submittable. And it never advances for work saved outside the wizard, so a
  // campaign with routes, artwork and a launch date still reports step one.
  //
  // So ask the campaign what is actually true and take whichever is further
  // along. The breadcrumb can still carry someone forward past a step they
  // legitimately skipped; this only ever unlocks, never locks.
  const hasRoutes = (distributionLocations ?? []).some((location) => (location?.selectedRoutes ?? []).length > 0);
  const hasDesign = Boolean(flyerInfo?.s3Link) && !notExistDesignFile;
  const hasStartDate = Boolean(campaign.startDate);

  const isStepSatisfied = (step: string): boolean => {
    if (step === "segments") return true;
    if (step === "design") return hasRoutes;
    if (step === "submit") return hasRoutes && hasDesign && hasStartDate;
    return false;
  };

  const reachedIndex = tabs.reduce(
    (furthest: number, item: any) => (isStepSatisfied(item.step) ? Math.max(furthest, item.index) : furthest),
    -1
  );
  const progressIndex = Math.max(completedStep?.index ?? -1, reachedIndex);

  // For EDDM, we no longer have a "details" step - it's combined with design
  const disabledStepsByFlyerDesignNotExisting = notExistDesignFile ? ["submit"] : [];
  const notVerified = isVerified === false;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(1200));
  const headerWithSkipLogic = (isActive: boolean, canBeActive: boolean, item: any, forbidDetails: boolean) => {
    if (internalStatus === CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW && item.step === "submit" && !isActive) {
      return <CompletedStepIcon />;
    }

    if (forbidDetails && !isMobile) {
      return <FutureCircle />;
    }
    return isActive ? (
      <CurrentStepIcon width={22} height={22} />
    ) : progressIndex >= item.index ? (
      <CompletedStepIcon />
    ) : (
      <FutureCircle />
    );
  };

  useEffect(() => {
    if (prevModalOpenRef.current && !isOpenWarningModal) {
      setShouldAnimate(true);
      const timer = setTimeout(() => {
        setShouldAnimate(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpenWarningModal]);

  useEffect(() => {
    prevModalOpenRef.current = isOpenWarningModal;
  }, [isOpenWarningModal]);

  // Topbar surface: off-white (#F9F9FF) with a soft shadow for the standard EDDM
  // flow, plain white with no shadow for the DM variant — both collapse to the
  // slate `card` token in dark. `border-solid box-border` lets `border-*`
  // utilities render without the global preflight reset (see cb-v2 note).
  const wrapperSurface = isDM
    ? "bg-white dark:bg-card"
    : "bg-[#F9F9FF] shadow-[0px_2px_14px_rgba(39,55,84,0.08)] dark:bg-card dark:shadow-none";

  return (
    <>
      <IfElse condition={accessDenied}>
        <div className={cn("relative top-0 z-[3] box-border h-auto w-full min-w-0 border-solid", wrapperSurface)} />
        <div
          className={cn(
            "relative top-0 z-[3] box-border h-auto w-full min-w-0 border-solid",
            "mob-header-wrapper",
            wrapperSurface
          )}
          onMouseEnter={() => setShouldHideStatistics(true)}
          onMouseLeave={() => setShouldHideStatistics(false)}
        >
          {isSubmitPage && <VerificationAlert />}
          {flyerSelectModalOpen && (
            <FlyerTypeModal
              setFlyerSelectModalOpen={setFlyerSelectModalOpen}
              flyerType={flyerType}
              campaignId={campaignId}
              userId={userId}
              clientName={clientName}
              submitEventName="Changed postcard format during map navigation"
            />
          )}
          <div
            className={cn(
              "relative grid h-20 w-full grid-cols-12 [grid-template-rows:100%] gap-4 px-5",
              "mob-grid-wrapper"
            )}
          >
            <div className={cn("col-[1/3] flex items-center", "home-back-icon mob-navigation-back-and-home")}>
              <div className="mr-0 md:mr-4">
                {/* Logo removed from the topbar — the redesigned oads-sidebar owns
                    the Oppizi logo now (avoids the duplicate logo on EDDM pages). */}
                <If condition={!hideBackButton}>
                  <div
                    onClick={handleBackClick}
                    className={cn(
                      "flex w-fit cursor-pointer items-center text-[#222336] transition-all dark:text-foreground",
                      "mob-navigation-height"
                    )}
                    // @ts-ignore
                    cypress_id={headerActions?.BACK?.cypressId}
                  >
                    <ArrowLeft className="mr-1 h-5 w-5" />
                    <span className={cn("text-base font-bold leading-5", "mob-hidden")}>Back</span>
                  </div>
                </If>
              </div>
            </div>
            <div className="col-[4/7] flex items-center justify-center min-[1100px]:col-[4/8] min-[1200px]:col-[5/8] min-[1400px]:col-[5/9]">
              <div className={cn("flex h-full items-center justify-center gap-4", "mob-tabs-wrapper")}>
                {tabs.map((item: any) => {
                  const isActive = activeTabIndex === item.index;
                  const nonActive = activeTabIndex !== item.index;
                  const errorDeletedDesign = disabledStepsByFlyerDesignNotExisting.includes(item.step);
                  const checkProcessedChannel = progressIndex >= item.index;
                  //This is needed cus the user can go to create design and back that for some reason counts as creating the flyer
                  // For EDDM (isDM), submit is now at index 3; for non-DM it's at index 4
                  const submitStepIndex = isDM ? 3 : 4;
                  const forbidDetails =
                    (item.index >= submitStepIndex &&
                      !isDM &&
                      pageState === FlyerStatus.flyerCreated &&
                      !isQrCodeExist &&
                      !flyerInfo?.s3Link) ||
                    isLoading;
                  const isSubmitTabInReview =
                    internalStatus === CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW && item.step === "submit";
                  const canBeActive = !hasError && (checkProcessedChannel || isSubmitTabInReview);
                  const isLink = canBeActive && !isActive && !forbidDetails;
                  return (
                    <Fragment key={item.index}>
                      <span
                        // Anchor per wizard step so Copilot control mode can move
                        // between them by name (campaignStep-design) instead of
                        // guessing at coordinates. The step key, not the label:
                        // labels are copy and will change.
                        data-cy={`campaignStep-${item.step}`}
                        aria-current={isActive ? "step" : undefined}
                        onClick={() => {
                          if (isLink) {
                            handleGoToTab(item.href);
                          }
                        }}
                        className={cn(
                          "flex h-full items-center gap-2 rounded-lg p-2.5",
                          "mob-item",
                          isLink && "cursor-pointer hover:h-11 hover:bg-[#E2E2FC] dark:hover:bg-accent",
                          { "display-none": nonActive, hidden: isMobile && nonActive }
                        )}
                      >
                        {!isMobile && headerWithSkipLogic(isActive, canBeActive, item, forbidDetails)}
                        <div
                          key={`${item.title}-${item.index}`}
                          className={cn("flex items-center", {
                            "relative h-[90%] flex-col justify-around": isMobile,
                          })}
                        >
                          <span
                            className={cn(
                              "text-sm",
                              (isActive || canBeActive) && !forbidDetails
                                ? isActive
                                  ? "font-semibold text-foreground"
                                  : "text-foreground"
                                : "text-slate-400",
                              isLink && "cursor-pointer"
                            )}
                          >
                            {item.title}
                          </span>
                          <StepDots totalSteps={tabs.length} currentStep={activeTabIndex} isMobile={isMobile} />
                        </div>
                      </span>
                      {item.index < tabs.length && (
                        <div
                          key={`seperator-${item.index}`}
                          className={cn("h-px w-12 bg-slate-200 dark:bg-accent", { hidden: isMobile })}
                        />
                      )}
                    </Fragment>
                  );
                })}
              </div>
            </div>
            <div className="col-[7/13] flex items-center justify-end gap-2 min-[900px]:col-[8/13] min-[1200px]:col-[9/13]">
              <CopilotLauncher />
              <ThemeToggle />
              <UserMenu clientId={clientId} companyName={clientName} />
            </div>
            {/* Cost summary + dropdown float BELOW the navbar (24px gap), top-right over
                the map — pulled out of the topbar so the navbar holds only the step nav,
                theme toggle and account menu. */}
            {/* Nothing to price or continue with until a location opens the map. */}
            {isSegmentsPage && !!distributionLocations?.length && (
              <div className="fixed right-6 top-[104px] z-[1200] flex">
                <EDDMSummary
                  grow={isInfoDropdownOpen}
                  setGrow={setIsInfoDropdownOpen}
                  isNextActive={isNextActive}
                  onContinue={headerActions.NEXT.action}
                  setFlyerSelectModalOpen={setFlyerSelectModalOpen}
                />
                <div
                  className={cn(
                    "relative flex h-[67px] w-[368px] items-center justify-end rounded-lg border border-solid border-[#F2F2F6] bg-card p-2.5 shadow-md dark:border-border",
                    !distributionLocations?.length ? "cursor-default" : "cursor-pointer"
                  )}
                  onClick={() =>
                    !distributionLocations?.length ? undefined : setIsInfoDropdownOpen(!isInfoDropdownOpen)
                  }
                >
                  <button
                    className={cn(
                      "absolute left-[-12px] top-5 flex h-6 w-6 items-center justify-center rounded-full border-0 bg-white text-foreground shadow-[0px_2px_14px_0px_#27375414] outline-none dark:bg-popover disabled:text-[rgba(16,16,16,0.3)]",
                      !distributionLocations?.length ? "cursor-default" : "cursor-pointer"
                    )}
                    disabled={!distributionLocations?.length}
                  >
                    {isInfoDropdownOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </button>
                  <div className="mr-[18px] flex h-10 flex-col items-end justify-between">
                    <div className="text-lg font-bold leading-[18px] text-foreground" data-copilot-readout="cost">
                      $ {formatNumber(subtotal)}
                    </div>
                    <p
                      data-copilot-readout="residences"
                      className={cn("whitespace-nowrap text-xs font-normal text-[#707087] dark:text-muted-foreground", {
                        "animate-[shake_0.5s_cubic-bezier(.36,.07,.19,.97)_both]": shouldAnimate,
                      })}
                    >
                      {formatNumber(flyersAmount) || "Set"} {flyersAmount ? "R" : "r"}esidences
                      {!isNextActive && (
                        <span
                          className={cn("text-sm font-normal text-[#A4262C] dark:text-destructive", {
                            "animate-[shake_0.5s_cubic-bezier(.36,.07,.19,.97)_both]": shouldAnimate,
                          })}
                        >
                          {" "}
                          (min: {CAMPAIGN_CHANNELS_MIN_RECIPIENTS[CAMPAIGN_SUBTYPES_ENUM.EDDM].toLocaleString()} res.)
                        </span>
                      )}
                    </p>
                  </div>
                  <button
                    className="h-[45px] w-[100px] cursor-pointer rounded-lg border-0 bg-primary text-center text-base font-bold text-primary-foreground"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      if (!isNextActive) {
                        setIsOpenWarningModal(true);
                      } else {
                        headerActions.NEXT.action();
                      }
                    }}
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}
          </div>
          {!isSubmitPage && tabs.length > 0 && <EddmStepBillingGate clientId={client?.id} surface="eddm-step" />}
        </div>
      </IfElse>

      <MinimumResidencesModal
        isOpen={isOpenWarningModal}
        onClose={() => setIsOpenWarningModal(false)}
        flyersAmount={flyersAmount}
      />
    </>
  );
};

interface StepDotsProps {
  totalSteps: number;
  currentStep: number;
  isMobile: boolean;
}

const StepDots = ({ totalSteps, currentStep, isMobile }: StepDotsProps) => {
  if (!isMobile) return null;
  return (
    <div className="flex items-center justify-center">
      {Array.from({ length: totalSteps }, (_, i) => (
        <span
          key={i + 1}
          className={cn(
            "mx-1 rounded-full",
            i + 1 === currentStep ? "h-2.5 w-2.5 bg-black dark:bg-foreground" : "h-2 w-2 bg-[#d3d3d3] dark:bg-slate-600"
          )}
        />
      ))}
    </div>
  );
};
